import { cn } from '../../lib/utils';
import type { PageFrameProps } from './types';

// The title lives here, in the page, not in the global top bar. Horizontal
// insets follow --page-margin; the canvas below is full-bleed and carries none.
export function PageFrame({
  title,
  eyebrow,
  backLink,
  subtitle,
  badge,
  action,
  controls,
  contextValue,
  command,
  className,
}: PageFrameProps) {
  return (
    // The canvas is a sibling, not a child: `<Page>` renders chrome above its
    // load/error gate and content below it. So the frame owns the gap beneath
    // itself — without it the controls sit on the first panel's hairline.
    <div className={cn('flex flex-col pb-8', className)}>
      {/* The back link takes the rhythm gap, so the title tucks under it as one
          unit rather than opening a second 32px step. */}
      {backLink ? <div className="px-[var(--page-margin)] pt-8">{backLink}</div> : null}
      <div
        className={cn('flex flex-col gap-2 px-[var(--page-margin)]', backLink ? 'pt-2' : 'pt-8')}
      >
        {eyebrow ? (
          <span className="text-[10px] uppercase leading-[12px] text-[var(--color-text-tertiary)]">{`[ ${eyebrow} ]`}</span>
        ) : null}
        <div className="flex items-center gap-4">
          <h1 className="text-[length:var(--eo-fs-title-sm)] uppercase leading-[var(--eo-lh-title-sm)] text-[var(--color-text-primary)] md:text-[length:var(--eo-fs-display-xs)] md:leading-[var(--eo-lh-display-xs)]">
            {title}
          </h1>
          {/* Shown at every width — the design draws it on the mobile frames too,
              so it is not a desktop-only affordance. */}
          {action ? <span className="inline-flex shrink-0">{action}</span> : null}
        </div>
        {subtitle || badge ? (
          <div className="flex flex-col items-start gap-3">
            {subtitle ? (
              <p className="text-xs leading-[14px] text-[var(--color-text-tertiary)]">{subtitle}</p>
            ) : null}
            {badge}
          </div>
        ) : null}
      </div>

      {controls || contextValue || command ? (
        // Reversed while stacked: the context value sits above the controls on
        // mobile and opposite them from md.
        <div className="flex flex-col-reverse gap-4 px-[var(--page-margin)] pt-8 md:flex-row md:items-center md:justify-between">
          {controls}
          {contextValue || command ? (
            <div className="flex flex-wrap items-center gap-3">
              {contextValue ? (
                <span className="text-xl text-[var(--color-text-primary)]">{contextValue}</span>
              ) : null}
              {command}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
